import { z } from 'zod'
import { normalizeHost } from './domain'
import { serpMarketSchema } from './serp/market'

const reasonMessage: Record<string, string> = {
  empty: 'Enter a domain',
  invalid: 'That does not look like a valid domain',
  'ip-address': 'Enter a domain name, not an IP address',
  'no-tld': 'Include a top-level domain, like example.com',
}

/** Accepts messy user input and yields the canonical host. */
export const hostSchema = z
  .string()
  .min(1)
  .max(300)
  .transform((value, ctx) => {
    const result = normalizeHost(value)
    if (!result.ok) {
      ctx.addIssue({ code: 'custom', message: reasonMessage[result.reason] ?? 'Invalid domain' })
      return z.NEVER
    }
    return result.host
  })

export const createSiteSchema = z.object({
  domain: hostSchema,
  displayName: z.string().trim().min(1).max(120).optional(),
})

/** Both fields optional because the two things that edit a site are a rename
 * dialog and a market select, and neither has any business resending the
 * other's value. */
export const updateSiteSchema = z.object({
  displayName: z.string().trim().min(1).max(120).optional(),
  serpMarket: serpMarketSchema.optional(),
})

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(80),
})

export const inviteMemberSchema = z.object({
  email: z.email('Enter a valid email address'),
  role: z.enum(['member', 'admin', 'owner']).default('member'),
})

export type CreateSiteInput = z.infer<typeof createSiteSchema>
export type UpdateSiteInput = z.infer<typeof updateSiteSchema>
